import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatDate } from "../lib/format";
import { useAlert, useConfirm } from "../components/Confirm";

/**
 * Adult Workforce Funding (AWF) tracker.
 *
 * SKELETON shipped 2026-05-20 ahead of a scope conversation with James. The
 * model is a tracker (status, funded vs actual hours, due date, notes) per PWS
 * per fiscal year. Once James pins down the detailed workflow we extend this
 * row rather than build something new.
 *
 * Access: 'awf' module — Commercial Finance + RGM by default.
 */
const STATUSES = ["due", "overdue", "in_progress", "submitted", "approved", "rejected", "na"] as const;
type Status = typeof STATUSES[number];

interface AwfRow {
  id: number;
  pws_id: number;
  pws_name: string;
  pws_initials: string;
  pws_acuity: string | null;
  home_name: string | null;
  funder_id: number | null;
  funder_name: string | null;
  fiscal_year: string;
  status: Status;
  weekly_hours_funded: number | null;
  weekly_hours_actual: number | null;
  gap_per_week_pence: number | null;
  review_due_date: string | null;
  last_reviewed_at: string | null;
  submitted_at: string | null;
  decision_at: string | null;
  notes: string | null;
  updated_at: string;
}

export function Awf() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["awf"], queryFn: api.awfList });
  const homes   = useQuery({ queryKey: ["homes"],   queryFn: api.homes });
  const funders = useQuery({ queryKey: ["funders"], queryFn: api.funders });
  const alertModal = useAlert();
  const confirm = useConfirm();

  const [filter, setFilter] = useState<"all" | Status>("all");
  const [drafts, setDrafts] = useState<Record<number, Partial<AwfRow>>>({});
  const [showAdd, setShowAdd] = useState(false);
  const [newRow, setNewRow] = useState<{ home_id: string; pws_id: string; funder_id: string; status: Status }>({ home_id: "", pws_id: "", funder_id: "", status: "due" });

  // PWS list scoped to the chosen home, so the modal isn't a 300-row dropdown.
  const peopleForNewRow = useQuery({
    queryKey: ["people", newRow.home_id],
    queryFn:  () => api.peopleByHome(newRow.home_id ? Number(newRow.home_id) : undefined),
    enabled:  !!newRow.home_id,
  });

  const all: AwfRow[] = list.data?.reviews ?? [];
  const counts: Record<string, number> = list.data?.counts ?? {};
  const fy: string = list.data?.fiscal_year ?? "FY27";

  const filtered = useMemo(
    () => filter === "all" ? all : all.filter((r) => r.status === filter),
    [all, filter],
  );

  function patchDraft(id: number, patch: Partial<AwfRow>) {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  }
  function rowValue<K extends keyof AwfRow>(row: AwfRow, key: K): AwfRow[K] {
    const d = drafts[row.id];
    return (d && key in d ? (d[key] as AwfRow[K]) : row[key]);
  }
  function isDirty(row: AwfRow): boolean {
    const d = drafts[row.id];
    if (!d) return false;
    return Object.keys(d).some((k) => (d as any)[k] !== (row as any)[k]);
  }

  async function onDelete(row: AwfRow) {
    const ok = await confirm({
      title: "Delete this review?",
      body: `The AWF review for ${row.pws_name || row.pws_initials || "this PWS"} will be removed. You can add it again later if needed.`,
      danger: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.awfDelete(row.id);
      await qc.invalidateQueries({ queryKey: ["awf"] });
    } catch (err) {
      await alertModal({ title: "Couldn't delete", body: String(err) });
    }
  }

  async function save(row: AwfRow) {
    try {
      await api.awfUpdate(row.id, {
        status:              rowValue(row, "status"),
        funder_id:           rowValue(row, "funder_id"),
        weekly_hours_funded: rowValue(row, "weekly_hours_funded"),
        weekly_hours_actual: rowValue(row, "weekly_hours_actual"),
        review_due_date:     rowValue(row, "review_due_date"),
        notes:               rowValue(row, "notes"),
      });
      setDrafts((d) => { const c = { ...d }; delete c[row.id]; return c; });
      await qc.invalidateQueries({ queryKey: ["awf"] });
    } catch (err) {
      await alertModal({ title: "Couldn't save", body: String(err) });
    }
  }

  async function createReview() {
    if (!newRow.pws_id) {
      await alertModal({ title: "Pick a PWS", body: "Choose the person we support this review covers." });
      return;
    }
    try {
      await api.awfCreate({
        pws_id:    Number(newRow.pws_id),
        funder_id: newRow.funder_id === "" ? null : Number(newRow.funder_id),
        status:    newRow.status,
      });
      setNewRow({ home_id: "", pws_id: "", funder_id: "", status: "due" });
      setShowAdd(false);
      await qc.invalidateQueries({ queryKey: ["awf"] });
    } catch (err) {
      await alertModal({ title: "Couldn't create", body: String(err) });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/" className="text-xs text-slate-500 hover:text-slate-700">← Back</Link>
        <div className="flex items-baseline justify-between mt-2 flex-wrap gap-3">
          <h1 className="text-2xl font-semibold text-liaise-navy">Adult Workforce Funding reviews <span className="text-slate-400 text-base font-normal">{fy}</span></h1>
          <button onClick={() => setShowAdd(true)} className="text-sm bg-liaise-blue text-white px-3 py-1.5 rounded-lg hover:bg-blue-700">+ Add review</button>
        </div>
        <p className="text-xs text-slate-500 mt-1 italic">
          Skeleton tracker - scope being firmed up with James. Track per-PWS reviews: funded hours vs actual, due date, status.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          <FilterChip current={filter} value="all"         label={`All (${all.length})`}                      onChange={setFilter} />
          <FilterChip current={filter} value="overdue"     label={`Overdue (${counts.overdue     ?? 0})`}     onChange={setFilter} />
          <FilterChip current={filter} value="due"         label={`Due (${counts.due             ?? 0})`}     onChange={setFilter} />
          <FilterChip current={filter} value="in_progress" label={`In progress (${counts.in_progress ?? 0})`} onChange={setFilter} />
          <FilterChip current={filter} value="submitted"   label={`Submitted (${counts.submitted ?? 0})`}     onChange={setFilter} />
          <FilterChip current={filter} value="approved"    label={`Approved (${counts.approved   ?? 0})`}     onChange={setFilter} />
          <FilterChip current={filter} value="rejected"    label={`Rejected (${counts.rejected   ?? 0})`}     onChange={setFilter} />
        </div>

        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">PWS / Home</th>
              <th className="text-left px-3 py-2.5 font-medium w-44">Funder</th>
              <th className="text-right px-3 py-2.5 font-medium w-24">Hrs funded</th>
              <th className="text-right px-3 py-2.5 font-medium w-24">Hrs actual</th>
              <th className="text-left  px-3 py-2.5 font-medium w-28">Due date</th>
              <th className="text-left  px-3 py-2.5 font-medium w-32">Status</th>
              <th className="text-left  px-3 py-2.5 font-medium">Notes</th>
              <th className="text-right px-3 py-2.5 font-medium w-24">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => {
              const dirty = isDirty(row);
              const funderId = rowValue(row, "funder_id");
              return (
                <tr key={row.id} className="border-t border-slate-100 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium text-liaise-navy">{row.pws_name} <span className="text-slate-400 text-xs">({row.pws_initials})</span></div>
                    <div className="text-xs text-slate-500">{row.home_name ?? "-"}{row.pws_acuity ? ` · ${row.pws_acuity} acuity` : ""}</div>
                  </td>
                  <td className="px-3 py-3">
                    <select className="num"
                      value={funderId ?? ""}
                      onChange={(e) => patchDraft(row.id, { funder_id: e.target.value === "" ? null : Number(e.target.value) })}
                    >
                      <option value="">No funder</option>
                      {(funders.data?.funders ?? []).map((f: any) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <input type="number" step={0.5} className="num"
                      value={rowValue(row, "weekly_hours_funded") ?? ""}
                      onChange={(e) => patchDraft(row.id, { weekly_hours_funded: e.target.value === "" ? null : Number(e.target.value) })} />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <input type="number" step={0.5} className="num"
                      value={rowValue(row, "weekly_hours_actual") ?? ""}
                      onChange={(e) => patchDraft(row.id, { weekly_hours_actual: e.target.value === "" ? null : Number(e.target.value) })} />
                  </td>
                  <td className="px-3 py-3">
                    <input type="date" className="num"
                      value={(rowValue(row, "review_due_date") ?? "").slice(0, 10)}
                      onChange={(e) => patchDraft(row.id, { review_due_date: e.target.value || null })} />
                  </td>
                  <td className="px-3 py-3">
                    <select className="num"
                      value={rowValue(row, "status")}
                      onChange={(e) => patchDraft(row.id, { status: e.target.value as Status })}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {row.submitted_at && <div className="text-[10px] text-orange-700 mt-1">Submitted {formatDate(row.submitted_at)}</div>}
                    {row.decision_at && <div className="text-[10px] text-emerald-700 mt-1">Decision {formatDate(row.decision_at)}</div>}
                  </td>
                  <td className="px-3 py-3">
                    <input type="text" className="w-full px-2 py-1 text-xs border border-slate-200 rounded"
                      placeholder="Notes"
                      value={rowValue(row, "notes") ?? ""}
                      onChange={(e) => patchDraft(row.id, { notes: e.target.value })} />
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button
                      disabled={!dirty}
                      onClick={() => save(row)}
                      className={`text-xs px-3 py-1.5 rounded ${
                        dirty ? "bg-liaise-blue text-white hover:bg-blue-700" : "bg-slate-100 text-slate-400 cursor-not-allowed"
                      }`}
                    >Save</button>
                    <button
                      onClick={() => onDelete(row)}
                      className="text-xs text-red-600 hover:underline ml-2"
                      title="Delete this AWF review"
                    >Delete</button>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr><td colSpan={8} className="text-center text-slate-400 px-4 py-10 text-sm">No reviews yet. Click "+ Add review" to start tracking one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-4">
            <h2 className="text-lg font-semibold text-liaise-navy">Add AWF review</h2>

            <label className="block">
              <div className="text-xs uppercase text-slate-500 font-medium mb-1.5">Home</div>
              <select className="input" value={newRow.home_id} onChange={(e) => setNewRow({ ...newRow, home_id: e.target.value, pws_id: "" })}>
                <option value="">Select a home...</option>
                {(homes.data?.homes ?? []).map((h: any) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <div className="text-xs uppercase text-slate-500 font-medium mb-1.5">Person we support</div>
              <select
                className="input"
                disabled={!newRow.home_id}
                value={newRow.pws_id}
                onChange={(e) => setNewRow({ ...newRow, pws_id: e.target.value })}
              >
                <option value="">{newRow.home_id ? "Select a PWS..." : "Pick a home first"}</option>
                {(peopleForNewRow.data?.people ?? [])
                  // Drop the empty / orphan records that show up as "() - -"
                  .filter((p: any) => (p.first_name?.trim() || p.last_name?.trim() || p.initials?.trim()))
                  .sort((a: any, b: any) => `${a.last_name ?? ""} ${a.first_name ?? ""}`.localeCompare(`${b.last_name ?? ""} ${b.first_name ?? ""}`))
                  .map((p: any) => {
                    const display = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.initials || `PWS ${p.id}`;
                    return (
                      <option key={p.id} value={p.id}>
                        {display}{p.initials ? ` (${p.initials})` : ""}
                      </option>
                    );
                  })}
              </select>
              {newRow.home_id && peopleForNewRow.isFetched && (peopleForNewRow.data?.people ?? []).length === 0 && (
                <div className="text-xs text-slate-400 mt-1">No people are recorded against this home yet.</div>
              )}
            </label>

            <label className="block">
              <div className="text-xs uppercase text-slate-500 font-medium mb-1.5">Funder (optional)</div>
              <select className="input" value={newRow.funder_id} onChange={(e) => setNewRow({ ...newRow, funder_id: e.target.value })}>
                <option value="">Inherit later</option>
                {(funders.data?.funders ?? []).map((f: any) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <div className="text-xs uppercase text-slate-500 font-medium mb-1.5">Starting status</div>
              <select className="input" value={newRow.status} onChange={(e) => setNewRow({ ...newRow, status: e.target.value as Status })}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button onClick={() => setShowAdd(false)} className="text-sm px-4 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={createReview} disabled={!newRow.pws_id} className="text-sm px-4 py-1.5 rounded-lg bg-liaise-blue text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">Create</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .num{padding:.35rem .5rem;border:1px solid rgb(226 232 240);border-radius:.375rem;font-size:.8125rem;width:100%;background:white}
        .input{display:block;width:100%;padding:.5rem .75rem;border:1px solid rgb(226 232 240);border-radius:.5rem;font-size:.875rem;background:white}
      `}</style>
    </div>
  );
}

function FilterChip({ current, value, label, onChange }: { current: string; value: any; label: string; onChange: (v: any) => void }) {
  return (
    <button
      onClick={() => onChange(value)}
      className={`text-xs px-3 py-1 rounded-full ${
        current === value ? "bg-liaise-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
      }`}
    >
      {label}
    </button>
  );
}
